import React, {
    forwardRef,
    useState,
} from 'react';
import {Chip, Grid, ButtonGroup, IconButton} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import PDFPreview from 'components/files/pdf/Preview';
import Show from 'components/utils/Show';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import {useGetRotateImage} from 'hooks/useGetEndpointActive';

const useStyles = makeStyles(theme => ({
    image: {
        cursor: 'zoom-in',
        [theme.breakpoints.down('xs')]: {
            maxWidth: '100%',
        },
    },
    loading: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '1em',

        '&::after': {
            content: `'Saving...'`,
            position: 'absolute',
            fontWeight: 'bold',
            color: theme.palette.gray.medium,
            transform: 'translate(0, 0)',
        },
        '&::before': {
            backgroundColor: 'white',
            content: `''`,
            opacity: '.5',
            position: 'absolute',
            width: '100%',
            height: '100%',
            zIndex: '1',
            transform: 'translate(0, 0)',
        },
    },
    controls: {
        marginTop: '1em',
    },
}));

const noPdfRotation = 'Rotation not supported on PDFs';

const ModalImage = forwardRef(
    ({alt, src, fullSrc, mimeType, filename, image_id}, containerRef) => {
        const classes = useStyles();
        const [rotation, setRotation] = useState(0);
        const [visualRotation, setVisualRotation] = useState(0);
        const rotate = useGetRotateImage();

        const showModalView = () => {
            window.open(fullSrc, 'full_document_view', 'popup');
        };

        const getMimeType = () => {
            if (mimeType) {
                return mimeType;
            }
            if (filename?.match('\\.pdf')) {
                return 'application/pdf';
            }
            return 'image/*';
        };

        const isPDF = getMimeType() === 'application/pdf';

        const doRotate = newRotation => {
            rotate.get(
                {
                    rotation_degs: newRotation % 360,
                },
                image_id
            ).then(() => {
                setRotation(0);
            });
        }

        return (
            <Grid container direction='column' alignItems='center'>
                <Grid item className={rotate.isLoading ? classes.loading : ''}>
                    <Show condition={isPDF}>
                        <PDFPreview
                            file={fullSrc}
                            ref={containerRef}
                            onClick={showModalView}
                            noPan
                        />
                    </Show>
                    <Show condition={!isPDF}>
                        <img
                            className={classes.image}
                            src={src}
                            alt={alt}
                            style={{
                                transform: `rotate(${visualRotation}deg)`,
                            }}
                            onClick={showModalView}
                        />
                    </Show>
                </Grid>
                <Grid item className={classes.controls}>
                    <Show condition={isPDF}>
                        <Chip
                            disabled
                            label={noPdfRotation}
                        />
                    </Show>
                    <Show condition={!isPDF}>
                        <ButtonGroup>
                            <IconButton
                                disabled={rotate.isLoading}
                                title='Rotate document left'
                                onClick={() => {
                                    const newRotate = rotation - 90;
                                    setRotation(newRotate);
                                    setVisualRotation(old => old - 90);
                                    doRotate(newRotate - rotation);
                                }}
                            >
                                <RotateLeftIcon />
                            </IconButton>
                            <IconButton
                                disabled={rotate.isLoading}
                                title='Rotate document right'
                                onClick={() => {
                                    const newRotate = rotation + 90;
                                    setRotation(newRotate);
                                    setVisualRotation(old => old + 90);
                                    doRotate(newRotate - rotation);
                                }}
                            >
                                <RotateRightIcon />
                            </IconButton>
                        </ButtonGroup>
                    </Show>
                </Grid>
            </Grid>
        );
    }
);

export default ModalImage;
